<script lang="ts">
  // Continuity module, Phase 1 — the OKR detail/drill-in panel (screen 5.3
  // of DESIGN), reached by clicking a row on OKRsPage. Same-page panel, not
  // a URL sub-route (see route.svelte.ts) — Phase 1 has exactly one list
  // screen, so there's nothing worth a shareable deep link yet.
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { ArrowLeft, Plus } from 'lucide-svelte';
  import { okrsApi } from '$lib/api/okrs';
  import { hrUsersApi } from '$lib/api/hr';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import StatusLamp from '$lib/components/ui/StatusLamp.svelte';
  import InfoTip from '$lib/components/ui/InfoTip.svelte';
  import type { ContinuityActionRow, OKRColour, TimeToReplace, ImpactIfLost } from '$lib/types';

  const { id, onBack, onChanged }: { id: number; onBack: () => void; onChanged: () => void } = $props();

  const LAMP_TONE: Record<OKRColour, 'bad' | 'warn' | 'good'> = { red: 'bad', amber: 'warn', green: 'good' };

  const TIME_TO_REPLACE_OPTIONS: { value: TimeToReplace; label: string }[] = [
    { value: '', label: 'Not set' },
    { value: 'under_1m', label: 'Under 1 month' },
    { value: '1_3m', label: '1–3 months' },
    { value: '3_6m', label: '3–6 months' },
    { value: '6_12m', label: '6–12 months' },
    { value: 'over_12m', label: 'Over 12 months' }
  ];
  const IMPACT_OPTIONS: { value: ImpactIfLost; label: string }[] = [
    { value: '', label: 'Not set' },
    { value: 'work_stops', label: 'Work stops' },
    { value: 'something_slips', label: 'Something slips' },
    { value: 'we_manage', label: 'We manage' }
  ];

  const TOOLTIP = {
    peopleWhoCanDoThis:
      'How many different people have actually done this in the last year, counted from Teams tasks. Not how many people we think could do it.',
    canAlsoDoThis:
      'People we know can do this OKR even if it does not show in Teams tasks. Use this when the work is not tracked as tasks.',
    backup: 'The person we believe could take this over. Until they have done it alone, this is a guess, not cover.',
    lastProven:
      'The last time the backup did this on their own, for real, while the main person was away. A handover meeting does not count.',
    recordProofTest:
      'Give the backup the real OKR for a day or a week. Log what happened, including if it went badly — that is the useful result.',
    timeToReplace:
      'From the day someone leaves to the day a replacement is doing it properly. Include notice period and learning time. A fresher from an institute replacing an experienced engineer is usually 12 months or more, not two.',
    linkedTaskTypes:
      'Which Teams tasks count as doing this OKR. Without this, we cannot work out how many people can do it.'
  };

  const queryClient = useQueryClient();
  const okrQuery = createQuery(() => ({ queryKey: ['okr', id], queryFn: () => okrsApi.get(id) }));
  const usersQuery = createQuery(() => ({ queryKey: ['hr-users'], queryFn: () => hrUsersApi.list() }));
  const taskOptionsQuery = createQuery(() => ({ queryKey: ['okr-task-options'], queryFn: () => okrsApi.taskOptions() }));

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['okr', id] });
    onChanged();
  }

  // ---- Details (name / group / description / time to replace / impact) ----
  let detailsForm = $state({ name: '', group: '', description: '', time_to_replace: '' as TimeToReplace, impact_if_lost: '' as ImpactIfLost });
  let detailsDirty = $state(false);
  let savingDetails = $state(false);

  $effect(() => {
    const o = okrQuery.data;
    if (o && !detailsDirty) {
      detailsForm = {
        name: o.name,
        group: o.group,
        description: o.description,
        time_to_replace: o.time_to_replace,
        impact_if_lost: o.impact_if_lost
      };
    }
  });

  async function saveDetails() {
    savingDetails = true;
    try {
      await okrsApi.update(id, detailsForm);
      detailsDirty = false;
      refresh();
      toast.success('Saved.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      savingDetails = false;
    }
  }

  // ---- Assignments (main / backups / can_do) ----
  let mainUserId = $state<number | null>(null);
  let backupUserIds = $state<number[]>([]);
  let canDoUserIds = $state<number[]>([]);
  let assignmentsDirty = $state(false);
  let savingAssignments = $state(false);

  $effect(() => {
    const o = okrQuery.data;
    if (o && !assignmentsDirty) {
      mainUserId = o.main?.id ?? null;
      // Pre-populate from the server's current backups/can_do sets — a save
      // PUTs each set in full (see okrsApi.setAssignments), so an
      // unpopulated picker would silently drop existing assignments not
      // re-checked (this was exactly the multi-backup bug: backups used to
      // be single-valued and never round-tripped).
      backupUserIds = [...o.backup_user_ids];
      canDoUserIds = [...o.can_do_user_ids];
    }
  });

  function toggleBackup(userId: number) {
    assignmentsDirty = true;
    backupUserIds = backupUserIds.includes(userId) ? backupUserIds.filter((i) => i !== userId) : [...backupUserIds, userId];
  }

  function toggleCanDo(userId: number) {
    assignmentsDirty = true;
    canDoUserIds = canDoUserIds.includes(userId) ? canDoUserIds.filter((i) => i !== userId) : [...canDoUserIds, userId];
  }

  async function saveAssignments() {
    savingAssignments = true;
    try {
      await okrsApi.setAssignments(id, { main_user_id: mainUserId, backup_user_ids: backupUserIds, can_do_user_ids: canDoUserIds });
      assignmentsDirty = false;
      refresh();
      toast.success('Assignments saved.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      savingAssignments = false;
    }
  }

  // ---- Linked task types ----
  let selectedTaskIds = $state<number[]>([]);
  let taskLinksDirty = $state(false);
  let savingTaskLinks = $state(false);

  $effect(() => {
    const o = okrQuery.data;
    if (o && !taskLinksDirty) {
      selectedTaskIds = o.task_links.map((l) => l.task_id);
    }
  });

  function toggleTask(taskId: number) {
    taskLinksDirty = true;
    selectedTaskIds = selectedTaskIds.includes(taskId) ? selectedTaskIds.filter((i) => i !== taskId) : [...selectedTaskIds, taskId];
  }

  async function saveTaskLinks() {
    savingTaskLinks = true;
    try {
      await okrsApi.setTaskLinks(id, selectedTaskIds);
      taskLinksDirty = false;
      refresh();
      toast.success('Linked task types saved.');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      savingTaskLinks = false;
    }
  }

  // ---- Proof tests ----
  let showProofTest = $state(false);
  let proofForm = $state({ user_id: 0, date: '', went_fine: true, notes: '' });
  let proofError = $state('');
  let savingProof = $state(false);

  function openProofTest() {
    proofForm = { user_id: okrQuery.data?.main?.id ?? 0, date: new Date().toISOString().slice(0, 10), went_fine: true, notes: '' };
    proofError = '';
    showProofTest = true;
  }

  async function submitProofTest() {
    if (!proofForm.user_id || !proofForm.date) {
      proofError = 'Who did it and the date are required.';
      return;
    }
    savingProof = true;
    proofError = '';
    try {
      await okrsApi.recordProofTest(id, proofForm);
      showProofTest = false;
      refresh();
      toast.success('Proof test recorded.');
    } catch (err) {
      proofError = getErrorMessage(err);
    } finally {
      savingProof = false;
    }
  }

  // ---- Actions ----
  let showActionModal = $state(false);
  let editingAction = $state<ContinuityActionRow | null>(null);
  let actionForm = $state({ description: '', owner_id: 0, due_date: '', status: 'open' as 'open' | 'done' });
  let actionError = $state('');
  let savingAction = $state(false);

  function openNewAction() {
    editingAction = null;
    actionForm = { description: '', owner_id: 0, due_date: '', status: 'open' };
    actionError = '';
    showActionModal = true;
  }

  function openEditAction(a: ContinuityActionRow) {
    editingAction = a;
    actionForm = { description: a.description, owner_id: a.owner_id, due_date: a.due_date || '', status: a.status };
    actionError = '';
    showActionModal = true;
  }

  async function submitAction() {
    if (!actionForm.description.trim() || !actionForm.owner_id) {
      actionError = 'Description and owner are required.';
      return;
    }
    savingAction = true;
    actionError = '';
    try {
      if (editingAction) {
        await okrsApi.updateAction(editingAction.id, actionForm);
      } else {
        await okrsApi.createAction(id, actionForm);
      }
      showActionModal = false;
      refresh();
      toast.success('Action saved.');
    } catch (err) {
      actionError = getErrorMessage(err);
    } finally {
      savingAction = false;
    }
  }

  async function toggleActionDone(a: ContinuityActionRow) {
    try {
      await okrsApi.updateAction(a.id, { status: a.status === 'done' ? 'open' : 'done' });
      refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }
</script>

<div class="tym-page">
  <button type="button" class="btn-secondary mb-4" onclick={onBack}>
    <ArrowLeft class="h-4 w-4" /> Back to OKRs
  </button>

  {#if okrQuery.isLoading}
    <div class="tym-loading-state">Loading...</div>
  {:else if okrQuery.isError}
    <div class="tym-error-state">Could not load this OKR.</div>
  {:else if okrQuery.data}
    {@const o = okrQuery.data}
    <div class="mb-5 flex items-center gap-2">
      <span title={o.status_reason}><StatusLamp tone={LAMP_TONE[o.colour]} label={o.status_reason} /></span>
      <h2 class="font-heading text-xl font-bold text-navy">{o.name || 'Untitled OKR'}</h2>
    </div>

    <div class="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <!-- Details -->
      <div class="tym-card space-y-4 p-5">
        <p class="tym-section-title">Details</p>
        <div>
          <div class="label">Name</div>
          <input type="text" class="input" bind:value={detailsForm.name} oninput={() => (detailsDirty = true)} />
        </div>
        <div>
          <div class="label">Group</div>
          <input type="text" class="input" bind:value={detailsForm.group} oninput={() => (detailsDirty = true)} />
        </div>
        <div>
          <div class="label">Description</div>
          <textarea class="input" rows="2" bind:value={detailsForm.description} oninput={() => (detailsDirty = true)}></textarea>
        </div>
        <div>
          <div class="label">
            Time to replace
            <InfoTip text={TOOLTIP.timeToReplace} />
          </div>
          <select class="input" bind:value={detailsForm.time_to_replace} onchange={() => (detailsDirty = true)}>
            {#each TIME_TO_REPLACE_OPTIONS as opt (opt.value)}
              <option value={opt.value}>{opt.label}</option>
            {/each}
          </select>
        </div>
        <div>
          <div class="label">If we lost this today</div>
          <select class="input" bind:value={detailsForm.impact_if_lost} onchange={() => (detailsDirty = true)}>
            {#each IMPACT_OPTIONS as opt (opt.value)}
              <option value={opt.value}>{opt.label}</option>
            {/each}
          </select>
        </div>
        <div class="flex justify-end">
          <button type="button" class="btn-primary" disabled={!detailsDirty || savingDetails} onclick={saveDetails}>
            {savingDetails ? 'Saving...' : 'Save details'}
          </button>
        </div>
      </div>

      <!-- Who does it / Backup / Can also do this -->
      <div class="tym-card space-y-4 p-5">
        <p class="tym-section-title">Who does it</p>
        {#if usersQuery.data}
          <div>
            <div class="label">Who does it</div>
            <select class="input" bind:value={mainUserId} onchange={() => (assignmentsDirty = true)}>
              <option value={null}>Nobody assigned</option>
              {#each usersQuery.data as u (u.id)}
                <option value={u.id}>{u.name || u.email}</option>
              {/each}
            </select>
          </div>
          <div>
            <div class="label">
              Backup
              <InfoTip text={TOOLTIP.backup} />
            </div>
            <div class="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-toyama-cloudy-gray p-2">
              {#each usersQuery.data as u (u.id)}
                <label class="flex items-center gap-2 text-[13px] text-toyama-heavy-carbon">
                  <input type="checkbox" class="h-4 w-4 accent-toyama-teal" checked={backupUserIds.includes(u.id)} onchange={() => toggleBackup(u.id)} />
                  {u.name || u.email}
                </label>
              {/each}
            </div>
          </div>
          <div>
            <div class="label">
              Can also do this
              <InfoTip text={TOOLTIP.canAlsoDoThis} />
            </div>
            <div class="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-toyama-cloudy-gray p-2">
              {#each usersQuery.data as u (u.id)}
                <label class="flex items-center gap-2 text-[13px] text-toyama-heavy-carbon">
                  <input type="checkbox" class="h-4 w-4 accent-toyama-teal" checked={canDoUserIds.includes(u.id)} onchange={() => toggleCanDo(u.id)} />
                  {u.name || u.email}
                </label>
              {/each}
            </div>
          </div>
          <div class="flex justify-end">
            <button type="button" class="btn-primary" disabled={!assignmentsDirty || savingAssignments} onclick={saveAssignments}>
              {savingAssignments ? 'Saving...' : 'Save who does it'}
            </button>
          </div>
        {/if}

        <div class="border-t border-toyama-cloudy-gray pt-3">
          <p class="text-[13px] font-semibold text-navy">
            People who can do this
            <InfoTip text={TOOLTIP.peopleWhoCanDoThis} />
          </p>
          <p class="text-2xl font-bold text-dark-gray">{o.people_who_can_do_this}</p>
          <p class="text-[12px] text-dark-gray">
            {o.people_auto_count} worked out from Teams tasks, {o.people_manual_count} added by hand.
          </p>
        </div>

        <div class="border-t border-toyama-cloudy-gray pt-3">
          <p class="text-[13px] font-semibold text-navy">
            Last proven
            <InfoTip text={TOOLTIP.lastProven} />
          </p>
          <p class="text-[13px] text-dark-gray">{o.last_proven || 'Never'}</p>
        </div>
      </div>

      <!-- Linked task types -->
      <div class="tym-card space-y-3 p-5">
        <p class="tym-section-title">
          Linked task types
          <InfoTip text={TOOLTIP.linkedTaskTypes} />
        </p>
        {#if taskOptionsQuery.data}
          <div class="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-toyama-cloudy-gray p-2">
            {#each taskOptionsQuery.data as t (t.id)}
              <label class="flex items-center gap-2 text-[13px] text-toyama-heavy-carbon">
                <input type="checkbox" class="h-4 w-4 accent-toyama-teal" checked={selectedTaskIds.includes(t.id)} onchange={() => toggleTask(t.id)} />
                {t.title} <span class="muted">— {t.owner_name}</span>
              </label>
            {:else}
              <p class="text-[13px] text-dark-gray">No tasks exist yet.</p>
            {/each}
          </div>
          <div class="flex justify-end">
            <button type="button" class="btn-primary" disabled={!taskLinksDirty || savingTaskLinks} onclick={saveTaskLinks}>
              {savingTaskLinks ? 'Saving...' : 'Save linked task types'}
            </button>
          </div>
        {/if}
      </div>

      <!-- Proof log -->
      <div class="tym-card space-y-3 p-5">
        <div class="flex items-center justify-between">
          <p class="tym-section-title">Proof log</p>
          <button type="button" class="btn-secondary" onclick={openProofTest}>
            <Plus class="h-3.5 w-3.5" /> Record a proof test
          </button>
        </div>
        <InfoTip text={TOOLTIP.recordProofTest} />
        <div class="tym-table-wrap">
          <div class="tym-table-scroll">
            <table class="tym-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Who did it alone</th>
                  <th>Went fine?</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {#each o.proof_log as p (p.id)}
                  <tr>
                    <td class="muted">{p.date}</td>
                    <td class="muted">{p.user_name}</td>
                    <td>{p.went_fine ? 'Yes' : 'No'}</td>
                    <td class="muted">{p.notes || '—'}</td>
                  </tr>
                {:else}
                  <tr><td class="empty" colspan="4">No proof tests recorded yet.</td></tr>
                {/each}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="tym-card space-y-3 p-5 lg:col-span-2">
        <div class="flex items-center justify-between">
          <p class="tym-section-title">Actions</p>
          <button type="button" class="btn-secondary" onclick={openNewAction}>
            <Plus class="h-3.5 w-3.5" /> Add action
          </button>
        </div>
        <div class="space-y-1.5">
          {#each o.actions as a (a.id)}
            <div class="flex items-center justify-between gap-2 rounded-lg border border-toyama-cloudy-gray px-3 py-2">
              <div>
                <p class="text-[13px] {a.status === 'done' ? 'text-dark-gray line-through' : 'text-navy'}">{a.description}</p>
                <p class="text-[11px] text-dark-gray">{a.owner_name}{a.due_date ? ` — due ${a.due_date}` : ''}</p>
              </div>
              <div class="flex shrink-0 gap-1.5">
                <button type="button" class="btn-secondary !px-2" onclick={() => openEditAction(a)}>Edit</button>
                <button type="button" class="btn-secondary !px-2" onclick={() => toggleActionDone(a)}>
                  {a.status === 'done' ? 'Reopen' : 'Done'}
                </button>
              </div>
            </div>
          {:else}
            <p class="text-[13px] text-dark-gray">No actions yet.</p>
          {/each}
        </div>
      </div>
    </div>
  {/if}
</div>

{#if showProofTest}
  <Modal title="Record a proof test" onClose={() => (showProofTest = false)}>
    <div class="space-y-4">
      <p class="text-[12px] text-dark-gray">{TOOLTIP.recordProofTest}</p>
      <div>
        <div class="label">Who did it alone</div>
        <select class="input" bind:value={proofForm.user_id}>
          <option value={0}>Select a person</option>
          {#each usersQuery.data || [] as u (u.id)}
            <option value={u.id}>{u.name || u.email}</option>
          {/each}
        </select>
      </div>
      <div>
        <div class="label">Date</div>
        <input type="date" class="input" bind:value={proofForm.date} />
      </div>
      <label class="flex items-center gap-2 text-[13px] text-toyama-heavy-carbon">
        <input type="checkbox" class="h-4 w-4 accent-toyama-teal" bind:checked={proofForm.went_fine} />
        Went fine
      </label>
      <div>
        <div class="label">Notes</div>
        <textarea class="input" rows="2" bind:value={proofForm.notes}></textarea>
      </div>
      {#if proofError}<div class="tym-error-state">{proofError}</div>{/if}
      <div class="flex justify-end gap-2 pt-1">
        <button type="button" class="btn-secondary" onclick={() => (showProofTest = false)}>Cancel</button>
        <button type="button" class="btn-primary" disabled={savingProof} onclick={submitProofTest}>
          {savingProof ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  </Modal>
{/if}

{#if showActionModal}
  <Modal title={editingAction ? 'Edit action' : 'Add action'} onClose={() => (showActionModal = false)}>
    <div class="space-y-4">
      <div>
        <div class="label">Description</div>
        <input type="text" class="input" bind:value={actionForm.description} />
      </div>
      <div>
        <div class="label">Owner</div>
        <select class="input" bind:value={actionForm.owner_id}>
          <option value={0}>Select an owner</option>
          {#each usersQuery.data || [] as u (u.id)}
            <option value={u.id}>{u.name || u.email}</option>
          {/each}
        </select>
      </div>
      <div>
        <div class="label">Due date</div>
        <input type="date" class="input" bind:value={actionForm.due_date} />
      </div>
      {#if editingAction}
        <div>
          <div class="label">Status</div>
          <select class="input" bind:value={actionForm.status}>
            <option value="open">Open</option>
            <option value="done">Done</option>
          </select>
        </div>
      {/if}
      {#if actionError}<div class="tym-error-state">{actionError}</div>{/if}
      <div class="flex justify-end gap-2 pt-1">
        <button type="button" class="btn-secondary" onclick={() => (showActionModal = false)}>Cancel</button>
        <button type="button" class="btn-primary" disabled={savingAction} onclick={submitAction}>
          {savingAction ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  </Modal>
{/if}
